import { Component, Input, ChangeDetectionStrategy } from '@angular/core';
import { IonItem, IonList, IonText } from '@ionic/angular';

import { UserAvatarComponent } from '@ninety/mobile/platform/components';
import { UserNamePipe } from '@ninety/mobile/platform/services';
import { TerraIconName, TerraStatusBadgeModule } from '@ninety/terra';

import { DiscussionListItem, DiscussionModel } from '../../models/discussion.models';
import { UserInitialsPipe } from '../../pipes/user-initials.pipe';

@Component({
  selector: 'nty-discussion-participants-status',
  imports: [IonItem, IonList, IonText, UserAvatarComponent, UserInitialsPipe, UserNamePipe, TerraStatusBadgeModule],
  template: `
    <ion-list class="terra-body" lines="none">
      <ion-text class="ctrl-label">Manager</ion-text>
      <ion-item class="terra-body">
        <div slot="start" class="user-name-with-avatar-container">
          <nty-user-avatar
            size="24px"
            fontSize="10px"
            [userInitials]="discussion.manager | userName | userInitials"
            [avatarUrl]="managerAvatarUrl" />
          <span class="participant-name">{{ discussion.manager | userName }}</span>
        </div>

        <terra-status-badge [color]="managerBadgeSeverity" slot="end">
          <terra-icon [icon]="managerBadgeIcon" />
          {{ discussion.isManagerDone ? 'Complete' : 'In Progress' }}
        </terra-status-badge>
      </ion-item>

      <ion-text class="ctrl-label">Team Member</ion-text>
      <ion-item class="terra-body">
        <div slot="start" class="user-name-with-avatar-container">
          <nty-user-avatar
            size="24px"
            fontSize="10px"
            [userInitials]="discussion.managee | userName | userInitials"
            [avatarUrl]="manageeAvatarUrl" />
          <span class="participant-name">{{ discussion.managee | userName }}</span>
        </div>

        <terra-status-badge [color]="manageeBadgeSeverity">
          <terra-icon [icon]="manageeBadgeIcon" />
          {{ discussion.isManageeDone ? 'Complete' : 'In Progress' }}
        </terra-status-badge>
      </ion-item>
    </ion-list>
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [
    `
      @use 'terra';

      ion-item {
        margin: 0 0 24px;
        --background: transparent;
      }

      .user-name-with-avatar-container {
        display: flex;
        align-items: center;
        gap: 6px;
        max-width: 180px;
      }

      .ctrl-label {
        display: block;
        margin-bottom: 6px;
        color: terra.color(text, light);
      }

      .participant-name {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        color: terra.color(text);
      }
    `,
  ],
})
export class DiscussionParticipantsStatusComponent {
  @Input({ required: true }) discussion!: DiscussionModel | DiscussionListItem;

  get managerAvatarUrl() {
    return this.discussion.manager?.metadata?.picture?.url ?? '';
  }

  get manageeAvatarUrl() {
    return this.discussion.managee?.metadata?.picture?.url ?? '';
  }

  get managerBadgeSeverity() {
    return this.discussion.isManagerDone ? 'success' : 'neutral';
  }

  get manageeBadgeSeverity() {
    return this.discussion.isManageeDone ? 'success' : 'neutral';
  }

  get managerBadgeIcon(): TerraIconName {
    return this.discussion.isManagerDone ? 'check' : 'edit';
  }

  get manageeBadgeIcon(): TerraIconName {
    return this.discussion.isManageeDone ? 'check' : 'edit';
  }
}
